import React from 'react';
import { ExternalLink } from 'lucide-react';
import { buttonVariants } from './ui/button';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { MaskedInput } from './ui/masked-input';
import { Autocomplete } from './ui/autocomplete';
import ClientSitesEditor from './ClientSitesEditor';
import useCepLookup from '../utils/useCepLookup';
import useLookup from '../utils/useLookup';
import { clientTypesService } from '../services/salesService';

export function emptyClientForm() {
    return {
        name: '',
        client_type_id: null,
        contact_name: '',
        cnpj: '',
        email: '',
        phone: '',
        whatsapp: '',
        zip_code: '',
        street: '',
        number: '',
        complement: '',
        city: '',
        state: '',
        status: 'active',
        sites: [],
    };
}

function ClientFormFields({ formData, onChange }) {
    const clientTypes = useLookup(() => clientTypesService.list());

    const { cepLoading, cepError, handleCepChange: lookupCep } = useCepLookup((data) => {
        onChange({ target: { name: 'street', value: data.street || formData.street || '' } });
        onChange({ target: { name: 'city', value: data.city || formData.city || '' } });
        onChange({ target: { name: 'state', value: data.state || formData.state || '' } });
    });

    const cnpjDigits = (formData.cnpj || '').replace(/\D/g, '');

    const handleCepChange = (e) => {
        onChange(e);
        lookupCep(e.target.value);
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome/Razão social *</label>
                    <Input name="name" value={formData.name} onChange={onChange} required />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contato</label>
                    <Input name="contact_name" value={formData.contact_name || ''} onChange={onChange} placeholder="Nome da pessoa de contato" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de cliente</label>
                    <Autocomplete
                        options={clientTypes}
                        getOptionLabel={(option) => option.name}
                        getOptionValue={(option) => option.id}
                        value={formData.client_type_id ?? null}
                        onChange={(value) => onChange({ target: { name: 'client_type_id', value: value ?? null } })}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CNPJ/CPF</label>
                    <div className="flex gap-2">
                        <MaskedInput mask="cpf-cnpj" name="cnpj" className="min-w-0 flex-1" value={formData.cnpj || ''} onChange={onChange} />
                        {cnpjDigits.length === 14 && (
                            <a
                                href={`https://cnpj.biz/${cnpjDigits}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(buttonVariants({ variant: 'outline' }), 'shrink-0')}
                            >
                                <ExternalLink className="w-4 h-4" />
                                Consultar
                            </a>
                        )}
                    </div>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
                    <Input type="email" name="email" value={formData.email || ''} onChange={onChange} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                    <MaskedInput mask="phone" name="phone" value={formData.phone || ''} onChange={onChange} placeholder="(00) 00000-0000" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp</label>
                    <MaskedInput mask="phone" name="whatsapp" value={formData.whatsapp || ''} onChange={onChange} placeholder="(00) 00000-0000" />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CEP</label>
                    <MaskedInput mask="cep" name="zip_code" value={formData.zip_code || ''} onChange={handleCepChange} />
                    {cepLoading && <p className="mt-1 text-xs text-gray-500">Buscando endereço...</p>}
                    {cepError && <p className="mt-1 text-xs text-rose-600">{cepError}</p>}
                </div>
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rua</label>
                    <Input name="street" value={formData.street || ''} onChange={onChange} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Número</label>
                    <Input name="number" value={formData.number || ''} onChange={onChange} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                    <Input name="complement" value={formData.complement || ''} onChange={onChange} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
                    <Input name="city" value={formData.city || ''} onChange={onChange} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">UF</label>
                    <Input name="state" maxLength={2} value={formData.state || ''} onChange={onChange} />
                </div>
            </div>

            <ClientSitesEditor
                sites={formData.sites || []}
                onChange={(sites) => onChange({ target: { name: 'sites', value: sites } })}
            />
        </div>
    );
}

export default ClientFormFields;
